// TODO: cleanup with concept_group_prevalence_preview ff

import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { theme } from '@verantos-dev/shared-styles';
import { borderRadius, fontWeight } from '@verantos-dev/shared-ui-components/tokens';

import { useConceptGroupPrevalence } from '../../api/prevalenceQueries';
import type { ConceptGroupDomainPrevalence } from '../../api/prevalenceTypes';

interface PrevalencePanelProps {
  conceptGroupId: string;
  /** Currently-pinned dataset release id (DATABASE.SCHEMA). Null when none. */
  datasetReleaseId?: string | null;
  /** User-facing dataset release name (catalog RELEASE_NAME). */
  datasetReleaseDisplayName?: string | null;
  /** Domains denormalized on the concept group (for skeleton row count). */
  domainsHint?: string;
}

// Keeps Domain | patients | events | % aligned across rows so a
// "0 patients" row visually matches a "1,619 patients" row. Raw px is
// layout, not spacing — no VDS token covers tabular column tracks
// (per VDS rule 4).
const ROW_GRID_TEMPLATE = '120px 120px 120px 80px';

/**
 * Per-domain prevalence rollup rendered above the concepts grid in the
 * ConceptGroupDetailsDialog. Gating happens in the parent dialog — this
 * component assumes it should fetch when mounted.
 */
export const PrevalencePanel = ({
  conceptGroupId,
  datasetReleaseId,
  datasetReleaseDisplayName,
  domainsHint,
}: PrevalencePanelProps) => {
  const intl = useIntl();
  const { data, isLoading, isError } = useConceptGroupPrevalence({
    conceptGroupId,
    datasetReleaseId,
  });

  // No dataset → no panel. Surfacing a "pin a dataset" hint only made
  // sense in chat where the user can act on it; in the cohort builder /
  // AV editor the user can't pin from the dialog, so show nothing.
  if (!datasetReleaseId) {
    return null;
  }

  const datasetLabel = datasetReleaseDisplayName ?? datasetReleaseId;

  if (isError) {
    return (
      <PanelShell>
        <Alert severity="warning">
          <Typography variant="body2" fontWeight={fontWeight.semibold}>
            {intl.formatMessage({ id: 'conceptGroup.prevalence.errorTitle' })}
          </Typography>
          <Typography variant="body2">
            {intl.formatMessage({ id: 'conceptGroup.prevalence.errorBody' }, { datasetLabel })}
          </Typography>
        </Alert>
      </PanelShell>
    );
  }

  if (isLoading || !data) {
    const placeholderRows = estimatePlaceholderRows(domainsHint);
    return (
      <PanelShell>
        <PanelHeader datasetLabel={datasetLabel} totalPersons={null} />
        <Stack gap={1}>
          {placeholderRows.map((domain) => (
            // TODO: no VDS token for this skeleton row height — needs designer input
            <Skeleton key={domain} variant="rectangular" height={28} />
          ))}
        </Stack>
      </PanelShell>
    );
  }

  if (data.domains.length === 0) {
    return (
      <PanelShell>
        <PanelHeader
          datasetLabel={datasetLabel}
          totalPersons={data.totalPersonsInDataset ?? null}
        />
        <Typography variant="body2" color={theme.palette.text.secondary}>
          {intl.formatMessage({ id: 'conceptGroup.prevalence.noDomains' })}
        </Typography>
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      <PanelHeader datasetLabel={datasetLabel} totalPersons={data.totalPersonsInDataset ?? null} />
      <Stack gap={1}>
        {data.domains.map((domain) => (
          <DomainRow key={domain.domain} row={domain} />
        ))}
      </Stack>
    </PanelShell>
  );
};

const PanelShell = ({ children }: { children: React.ReactNode }) => (
  <Box
    data-testid="concept-group-prevalence-panel"
    sx={{
      // TODO: no VDS token for hairline border width — needs designer input
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: `${borderRadius.md}px`,
      padding: 2,
      backgroundColor: theme.palette.background.default,
    }}
  >
    <Stack gap={2}>{children}</Stack>
  </Box>
);

const PanelHeader = ({
  datasetLabel,
  totalPersons,
}: {
  datasetLabel: string;
  totalPersons: number | null;
}) => {
  const intl = useIntl();
  return (
    <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ rowGap: 1 }}>
      <Typography variant="subtitle2" color={theme.palette.text.primary}>
        {intl.formatMessage({ id: 'conceptGroup.prevalence.title' })}
      </Typography>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="body2" color={theme.palette.text.secondary}>
          {intl.formatMessage({ id: 'conceptGroup.prevalence.dataset' })}
        </Typography>
        <Typography variant="body2" color={theme.palette.text.primary}>
          {datasetLabel}
        </Typography>
      </Stack>
      {totalPersons !== null && (
        <Typography variant="body2" color={theme.palette.text.secondary}>
          {intl.formatMessage(
            { id: 'conceptGroup.prevalence.totalPatients' },
            { count: totalPersons }
          )}
        </Typography>
      )}
    </Stack>
  );
};

const DomainRow = ({ row }: { row: ConceptGroupDomainPrevalence }) => {
  const intl = useIntl();
  return (
    <Box
      data-testid={`concept-group-prevalence-row-${row.domain}`}
      style={{
        display: 'grid',
        gridTemplateColumns: ROW_GRID_TEMPLATE,
      }}
      sx={{
        alignItems: 'center',
        columnGap: 2,
        paddingY: 1,
        // TODO: no VDS token for hairline border width — needs designer input
        borderTop: `1px solid ${theme.palette.divider}`,
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Typography variant="body2" color={theme.palette.text.primary}>
        {row.domain}
      </Typography>
      <Typography variant="body2" color={theme.palette.text.primary}>
        {intl.formatMessage(
          { id: 'conceptGroup.prevalence.patientsCount' },
          { count: row.nSubjects }
        )}
      </Typography>
      <Typography variant="body2" color={theme.palette.text.secondary}>
        {intl.formatMessage({ id: 'conceptGroup.prevalence.eventsCount' }, { count: row.nEvents })}
      </Typography>
      <Typography variant="body2" color={theme.palette.text.secondary}>
        {typeof row.prevalencePct === 'number'
          ? `${intl.formatNumber(row.prevalencePct, { maximumFractionDigits: 3 })}%`
          : ''}
      </Typography>
    </Box>
  );
};

const estimatePlaceholderRows = (domainsHint: string | undefined): string[] => {
  if (!domainsHint) return ['Condition'];
  return domainsHint
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean)
    .slice(0, 6);
};

export default PrevalencePanel;
